import { useCallback, useEffect, useState } from 'react';

interface ViewportFitOptions {
  /** Space to leave below the element, in px. */
  bottomGap?: number;
  /** Never shrink below this height, in px. */
  min?: number;
}

/**
 * Height that makes the element run from where it currently sits on screen to
 * the bottom of the visible viewport. Measured from the visual viewport, so
 * browser toolbars on iPad and phones (which `vh` ignores) can't push the
 * bottom of the element out of view. Returns undefined while the element is
 * hidden (e.g. `display: none` below a breakpoint).
 */
export function useViewportFitHeight<T extends HTMLElement>({
  bottomGap = 0,
  min = 0,
}: ViewportFitOptions = {}): [(node: T | null) => void, number | undefined] {
  const [node, setNode] = useState<T | null>(null);
  const [height, setHeight] = useState<number | undefined>();

  useEffect(() => {
    if (!node) return;
    let frame = 0;

    const measure = () => {
      frame = 0;
      if (!node.getClientRects().length) {
        setHeight(undefined);
        return;
      }
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const top = node.getBoundingClientRect().top;
      setHeight(Math.max(min, Math.floor(viewportHeight - top - bottomGap)));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    measure();
    // Content above the element (filters, loaded lists) can move it without a scroll.
    const layoutObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    layoutObserver?.observe(document.body);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('scroll', schedule);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      layoutObserver?.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('scroll', schedule);
    };
  }, [node, bottomGap, min]);

  const ref = useCallback((el: T | null) => setNode(el), []);
  return [ref, height];
}
